import { Combobox, Input, Label, Select } from "@coideo/barrani";
import { LocationMarkerIcon } from "@heroicons/react/outline";
import { type EventListItem, useEvents } from "hooks/event";
import { useVenues, type Venue } from "hooks/venue";
import { Button, Link, Loader } from "lib";
import { useTranslation } from "next-i18next";
import { type ComponentType, Fragment, type ReactNode, useState } from "react";
import { withinNDays } from "utils/dates";

import EmptyState from "./EmptyState";

function VenueFilter({ onChange }: { onChange: (v: string | undefined) => void }) {
  const { t } = useTranslation();
  const { data: venues = [], search } = useVenues();
  const [selected, setSelected] = useState<Venue | null>(null);

  return (
    <Combobox
      nullable
      displayValue={(v) => v?.name ?? ""}
      label={t("generic.venue")}
      placeholder={t("generic.venuePlaceholder")}
      value={selected}
      onChange={(v) => {
        setSelected(v);
        onChange(v?.id.toString());
      }}
      onSearch={(v) => {
        search(v.toString());
      }}
    >
      <Combobox.List>
        {venues.length > 0 ? (
          venues.map((v) => (
            <Combobox.Item key={v.id} value={v}>
              {v.name}
            </Combobox.Item>
          ))
        ) : (
          <span className="block cursor-default select-none truncate py-2 pl-3 pr-9 text-sm font-normal text-gray-900">
            {t("generic.noResults")}
          </span>
        )}
      </Combobox.List>
    </Combobox>
  );
}

const dateFilters = [
  { id: "X", filter: { dateTo: null } },
  { id: "7", filter: { dateTo: withinNDays(7) } },
  { id: "15", filter: { dateTo: withinNDays(15) } },
  { id: "30", filter: { dateTo: withinNDays(30) } },
  { id: "90", filter: { dateTo: withinNDays(90) } },
] as const;

function DateFilter({ onChange }: { onChange: (v: { dateTo: string | null }) => void }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(dateFilters[0]);

  return (
    <Select
      className="sm:w-56"
      displayValue={(filter) => (filter ? t(`dateFilters.${filter.id}`) : null)}
      label={t("generic.period")}
      value={selected}
      onChange={(filter) => {
        setSelected(filter);
        onChange(filter.filter);
      }}
    >
      {dateFilters.map((filter) => (
        <Select.Item key={filter.id} value={filter}>
          {t(`dateFilters.${filter.id}`)}
        </Select.Item>
      ))}
      {/* <button
          className="flex items-center w-full px-3 py-2 text-gray-900 bg-gray-100 border border-gray-400 focus:bg-gray-300 hover:bg-gray-300 focus:ring-primary-500"
          onClick={() => alert('TODO: falta hacer')}
        >
          <CalendarIcon className='w-4 h-4 mr-1 text-gray-400' />
          Elegir rango
        </button> */}
    </Select>
  );
}

function EventsList({
  component: Component,
}: {
  component: ComponentType<{ event: EventListItem }>;
}) {
  const { t } = useTranslation();
  const {
    data: { pages = [] } = {},
    search,
    queryString,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useEvents();

  return (
    <div className="mx-auto max-w-7xl space-y-3 px-4 sm:px-0">
      <div className="mb-6 flex flex-col space-x-0 space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
        <div className="space-y-1">
          <Label htmlFor="search-date">{t("generic.firstName")}</Label>
          <Input
            id="search-events"
            placeholder={t("generic.eventName")}
            onChange={(e) => {
              search({ search: e.target.value });
            }}
          />
        </div>
        <VenueFilter
          onChange={(venueId) => {
            search({ venue: venueId });
          }}
        />
        <DateFilter
          onChange={(dateFilter) => {
            search({ dateTo: dateFilter.dateTo ?? undefined });
          }}
        />
      </div>
      {isLoading ? (
        <div className="py-10">
          <Loader />
        </div>
      ) : pages.length === 0 ? (
        <EmptyState value={queryString.search} />
      ) : (
        <>
          <ul className="space-y-3">
            {pages.map((group, i) => (
              <Fragment key={i}>
                {group.map((event) => (
                  <Component key={event.id} event={event} />
                ))}
              </Fragment>
            ))}
          </ul>
          <nav className="flex items-center justify-end px-2 py-3 sm:px-8">
            <Button
              disabled={!hasNextPage || isFetchingNextPage}
              kind="light"
              loading={isFetchingNextPage}
              onClick={() => {
                void fetchNextPage();
              }}
            >
              {hasNextPage ? t("pagination.loadMore") : t("pagination.noMore")}
            </Button>
          </nav>
        </>
      )}
    </div>
  );
}

function EventItem({ event, children }: { event: EventListItem; children: ReactNode }) {
  return (
    <li className="block overflow-hidden rounded bg-white shadow hover:bg-gray-50 focus:bg-gray-50 focus:outline-none">
      <div className="flex flex-col content-center sm:flex-row">
        <div className="shrink-0 ">
          <img
            alt={`${event.name} logo`}
            className="h-40 w-full object-cover sm:h-full sm:w-40"
            src={event.thumbImage}
          />
        </div>
        <div className="flex flex-col justify-center p-6 sm:w-1/2">
          <Link
            className="cursor-pointer truncate rounded-md text-lg font-semibold text-gray-800 hover:text-primary-800 focus:outline-none sm:text-lg"
            href={`/events/${event.id}/sale`}
          >
            {event.name}
          </Link>
          <div className="mt-2 flex items-center text-sm text-gray-500">
            <LocationMarkerIcon className="mr-1 h-4 w-4 text-gray-400" />
            <span className="truncate">{event.venue.description}</span>
          </div>
        </div>
        <div className="flex items-center p-6 sm:w-1/2 sm:justify-end">
          <div className="flex flex-wrap items-center justify-end gap-2">{children}</div>
        </div>
      </div>
    </li>
  );
}

const Events = {
  List: EventsList,
  Item: EventItem,
};

export default Events;
